import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  HubItemChangeActiveState,
  HubItemCreate,
  HubItemDelete,
  HubItemInitialState,
  HubItemNewSsccId,
  HubItemSetId,
} from './hub-item.actions';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { NotificationService } from '../../../../../common/services';
import { HubItemService } from './hub-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { HubOverviewLoad } from './overview/hub-overview.actions';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HubItemStateModel {
  id: number;
  newSsccId: number;
}

const initialState: HubItemStateModel = {
  id: null,
  newSsccId: null,
};

@State<HubItemStateModel>({
  name: 'hubItem',
  defaults: initialState,
})
@Injectable()
export class HubItemState {
  constructor(
    private hubItemService: HubItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: HubItemStateModel): HubItemStateModel {
    return state;
  }

  @Selector()
  static getHubId(state: HubItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getSscdIdForNewHub(state: HubItemStateModel): number {
    return state.newSsccId;
  }

  @Action(HubItemSetId)
  setHubId(ctx: StateContext<HubItemStateModel>, { payload }: HubItemSetId): void {
    ctx.patchState({ id: payload });
  }

  @Action(HubItemCreate)
  createNewHub(
    ctx: StateContext<HubItemStateModel>,
    { payload }: HubItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubItemService.createNewHub(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.hub.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/hubs');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubItemChangeActiveState)
  changeHubActiveState(
    ctx: StateContext<HubItemStateModel>,
    { payload: { hubId, enable } }: HubItemChangeActiveState,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.hubItemService.changeHubActiveState(hubId, enable).pipe(
      take(1),
      map(() => {
        this.notificationService.success(`api.hub.success.${enable ? 'activate' : 'deactivate'}`);
        return ctx.dispatch(new HubOverviewLoad(hubId));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubItemDelete)
  deleteHub(
    ctx: StateContext<HubItemStateModel>,
    { payload }: HubItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.hubItemService.deleteHub(payload.hubId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.hub.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/hubs');
        return ctx.dispatch(new HubItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubItemNewSsccId)
  getNewSsccId(ctx: StateContext<HubItemStateModel>): Observable<MatSnackBarRef<SimpleSnackBar> | HubItemStateModel> {
    return this.hubItemService.getNewSsccId().pipe(
      take(1),
      map((newSsccId: number) => ctx.patchState({ newSsccId })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubItemInitialState)
  setInitialState(ctx: StateContext<HubItemInitialState>): void {
    ctx.setState(initialState);
  }
}
